import React, { useState } from 'react'
import { Card } from '../ui'
import { AgendaItem } from './AgendaItem.jsx'
import { useAgenda } from './hooks/useAgenda.js'
import { useImportOpenItems } from './hooks/useImportOpenItems.js'

export function AgendaCard({
  sessionId,
  reportId,
  initialItems,
  locked,
  currentUserId,
  otherUserName,
  onSaved,
}) {
  const { items, addItem, removeItem, toggleChecked, setPriority, setText, commitItem, reorderItems, importItems } =
    useAgenda({ sessionId, reportId, initialItems, locked, onSaved })
  const { fetchOpenItems, loading: importLoading } = useImportOpenItems({ sessionId })
  const [importEmpty, setImportEmpty] = useState(false)

  async function handleImport() {
    setImportEmpty(false)
    const openItems = await fetchOpenItems()
    if (!openItems.length) {
      setImportEmpty(true)
      return
    }
    importItems(openItems)
  }

  const [dragIdx, setDragIdx] = useState(null)
  const [dragOverIdx, setDragOverIdx] = useState(null)

  function handleDragStart(e, idx) {
    setDragIdx(idx)
    e.dataTransfer.effectAllowed = 'move'
  }

  function handleDragOver(e, idx) {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (idx !== dragIdx) setDragOverIdx(idx)
  }

  function handleDrop(e, idx) {
    e.preventDefault()
    if (dragIdx === null || dragIdx === idx) return
    reorderItems(dragIdx, idx)
    setDragIdx(null)
    setDragOverIdx(null)
  }

  function handleDragEnd() {
    setDragIdx(null)
    setDragOverIdx(null)
  }

  return (
    <Card style={{ padding: '8px 4px' }}>
      {items.length === 0 && locked ? (
        <p style={styles.empty}>No agenda items.</p>
      ) : (
        <div style={styles.list}>
          {items.map((item, idx) => {
            const canDrag = !locked && !item.checked
            return (
              <div
                key={item.id}
                draggable={canDrag}
                onDragStart={canDrag ? (e) => handleDragStart(e, idx) : undefined}
                onDragOver={canDrag ? (e) => handleDragOver(e, idx) : undefined}
                onDrop={canDrag ? (e) => handleDrop(e, idx) : undefined}
                onDragEnd={canDrag ? handleDragEnd : undefined}
              >
                <AgendaItem
                  item={item}
                  locked={locked}
                  currentUserId={currentUserId}
                  otherUserName={otherUserName}
                  onToggleChecked={toggleChecked}
                  onSetPriority={setPriority}
                  onSetText={setText}
                  onCommit={commitItem}
                  onRemove={removeItem}
                  isDragging={dragIdx === idx}
                  isDragOver={dragOverIdx === idx}
                  dragHandleProps={{
                    draggable: false,
                    onMouseDown: (e) => e.stopPropagation(),
                  }}
                />
              </div>
            )
          })}
        </div>
      )}

      {!locked && (
        <div style={styles.actions}>
          <button style={styles.addBtn} onClick={() => addItem(currentUserId)}>
            + add item
          </button>
          <button
            style={styles.importBtn}
            onClick={handleImport}
            disabled={importLoading}
          >
            {importLoading ? 'importing…' : 'import open items'}
          </button>
          {importEmpty && (
            <span style={styles.importEmpty}>no open items found</span>
          )}
        </div>
      )}
    </Card>
  )
}

const styles = {
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  empty: {
    fontSize: 12,
    color: 'var(--text-secondary)',
    padding: '8px 12px',
    margin: 0,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginTop: 6,
    marginLeft: 46,
  },
  addBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: 11,
    color: '#c4783b',
    padding: '2px 0',
    fontWeight: 500,
  },
  importBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: 11,
    color: 'var(--text-secondary)',
    padding: '2px 0',
    fontWeight: 500,
  },
  importEmpty: {
    fontSize: 11,
    color: 'var(--text-secondary)',
    fontStyle: 'italic',
  },
}
